import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMoeda } from "../../../../core/hooks/useMoeda";
import { IProdutoPedido } from "../../../interface/IProdutoPedido";

export type GrupoProdutoPedido =
    | { type: "single"; produto: IProdutoPedido }
    | { type: "combo"; comboUuid: string; comboDescricao?: string; items: IProdutoPedido[] };

/** Preço de um item somado aos seus complementos, sem multiplicar pela quantidade. */
const precoUnitarioComItens = (produto: IProdutoPedido): number => {
    let preco = produto.preco ?? 0;
    produto.itens?.forEach(item => {
        preco += (item.preco ?? 0) * (item.quantidade ?? 1);
    });
    return preco;
};

const LinhaProduto = ({ produto, aninhado }: { produto: IProdutoPedido; aninhado?: boolean }) => {
    const { real } = useMoeda();
    const total = precoUnitarioComItens(produto) * (produto.quantidade ?? 1);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "baseline" }}>
                <Typography sx={{ fontSize: aninhado ? "0.8125rem" : "0.875rem", minWidth: 0 }}>
                    {/* A quantidade destacada acelera a conferência item a item. */}
                    <Box component="span" sx={{ fontWeight: 700, mr: 0.75 }}>
                        {produto.quantidade}×
                    </Box>
                    {produto.descricao}
                </Typography>
                <Typography
                    sx={{
                        fontSize: aninhado ? "0.8125rem" : "0.875rem",
                        fontWeight: aninhado ? 400 : 600,
                        whiteSpace: "nowrap",
                        color: aninhado ? "text.secondary" : "text.primary",
                    }}
                >
                    {real(total)}
                </Typography>
            </Box>

            {!!produto.itens?.length && (
                <Box sx={{ pl: 1.5, display: "flex", flexDirection: "column", gap: 0.125 }}>
                    {produto.itens.map(item => (
                        <Typography
                            key={item.uuid ?? item.descricao}
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.75rem" }}
                        >
                            {item.quantidade}× {item.descricao}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

/**
 * Itens do pedido, com os combos agrupados.
 *
 * As descrições deixaram de ser exibidas em caixa alta: nome de produto em maiúsculas inteiras
 * perde o formato das palavras e fica mais lento de ler numa conferência.
 */
export const SecaoProdutos = ({ grupos }: { grupos: GrupoProdutoPedido[] }) => {
    const { real } = useMoeda();

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
            {grupos.map(grupo => {
                if (grupo.type === "combo") {
                    const totalCombo = grupo.items.reduce(
                        (acumulado, item) => acumulado + precoUnitarioComItens(item) * (item.quantidade ?? 1),
                        0
                    );

                    return (
                        <Box
                            key={grupo.comboUuid}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                padding: 1.5,
                                borderRadius: "10px",
                                border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                backgroundColor: theme => alpha(theme.palette.primary.main, 0.03),
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "baseline" }}>
                                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "primary.main" }}>
                                    {grupo.comboDescricao ?? "Combo"}
                                </Typography>
                                <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {real(totalCombo)}
                                </Typography>
                            </Box>

                            {grupo.items.map(item => (
                                <LinhaProduto key={item.uuid} produto={item} aninhado />
                            ))}
                        </Box>
                    );
                }

                return <LinhaProduto key={grupo.produto.uuid} produto={grupo.produto} />;
            })}
        </Box>
    );
};
